import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Lock, Sparkles } from "lucide-react";
import { PublicShareResponse, fileUrl, sharesApi } from "@/lib/catalog-api";
import { ApiError } from "@/lib/api";
import { Wordmark } from "@/components/brand/Wordmark";

export default function PublicShare() {
  const { token } = useParams();
  const [data, setData] = useState<PublicShareResponse | null>(null);
  const [error, setError] = useState<{ status: number; message: string } | null>(null);

  useEffect(() => {
    if (!token) return;
    sharesApi.publicView(token)
      .then(setData)
      .catch((e) => {
        if (e instanceof ApiError) {
          setError({ status: e.status, message: e.message });
        } else {
          setError({ status: 0, message: "Could not load this lookbook." });
        }
      });
  }, [token]);

  if (error) {
    return (
      <main className="min-h-screen bg-background grid place-items-center px-6" data-testid="public-share-error">
        <div className="max-w-md text-center">
          <Lock className="w-7 h-7 mx-auto text-oxblood" strokeWidth={1.4} />
          <h1 className="font-display text-3xl mt-4">
            {error.status === 410 ? "This link is no longer active." :
             error.status === 404 ? "We couldn't find this lookbook." :
             error.status === 429 ? "Slow down for a minute." :
             "Something went wrong."}
          </h1>
          <p className="text-sm text-muted-foreground mt-3">{error.message}</p>
        </div>
      </main>
    );
  }

  if (!data) {
    return (
      <main className="min-h-screen bg-background px-6 md:px-12 py-12" data-testid="public-share-loading">
        <div className="h-6 w-32 bg-bone animate-pulse mb-4" />
        <div className="h-12 w-2/3 bg-bone animate-pulse" />
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-background" data-testid="public-share-page">
      <header className="border-b border-border px-6 md:px-12 py-5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          {data.workspace_logo_url ? (
            <img src={fileUrl(data.workspace_logo_url)} alt={data.workspace_name}
              className="w-8 h-8 object-cover bg-bone" />
          ) : null}
          <p className="text-[11px] uppercase tracking-[0.3em] text-muted-foreground">
            A lookbook from <span className="text-foreground">{data.workspace_name}</span>
          </p>
        </div>
        <a href="/" data-testid="share-wordmark" className="opacity-90 hover:opacity-100 transition-opacity">
          <Wordmark className="h-7" />
        </a>
      </header>

      <section className="px-6 md:px-12 py-14 max-w-6xl">
        <p className="text-[11px] uppercase tracking-[0.28em] text-oxblood font-medium mb-4 flex items-center gap-2">
          <Sparkles className="w-3.5 h-3.5" /> {data.occasion || "Lookbook"}
        </p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-balance leading-[1.05]"
          data-testid="public-share-title">{data.title}</h1>
        {data.description && (
          <p className="mt-6 max-w-prose text-base leading-relaxed text-foreground/80"
            data-testid="public-share-description">
            {data.description}
          </p>
        )}

        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
          data-testid="public-share-items">
          {data.items.map((it, i) => (
            <article key={i} className="bg-background">
              <div className="aspect-[4/5] bg-bone overflow-hidden">
                {it.primary_photo_url ? (
                  <img src={fileUrl(it.primary_photo_url)} alt={it.name} loading="lazy"
                    className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full grid place-items-center text-[10px] uppercase tracking-[0.2em] text-muted-foreground">No photo</div>
                )}
              </div>
              <div className="mt-3">
                <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                  {it.brand || it.category || "—"}
                </p>
                <p className="font-display text-lg leading-snug mt-1">{it.name}</p>
              </div>
            </article>
          ))}
        </div>

        <footer className="mt-20 border-t border-border pt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
          data-testid="public-share-footer">
          <p className="text-[11px] text-muted-foreground/80">
            Shared on {new Date(data.created_at).toLocaleDateString()}
          </p>
          <a
            href="/"
            data-testid="public-share-cta"
            className="text-[11px] text-muted-foreground/90 hover:text-oxblood transition-colors flex items-center gap-2"
          >
            <span className="font-display tracking-tight text-foreground/80">Wardrobe Index</span>
            <span className="opacity-60">·</span>
            <span>Made with Wardrobe Index · Try it free →</span>
          </a>
        </footer>
      </section>
    </main>
  );
}
